import { useState, useRef, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Upload, FileCheck, AlertCircle, RefreshCw } from 'lucide-react';
import { useDiagnosticContext } from '../../../context/DiagnosticContext';
import { useAuth } from '../../../context/AuthContext';
import { PCCUtils, PCCData, parsePccFile } from '../../../utils/PCCUtils';
import { trackEvent } from '../../../services/firebase';
import '../DiagnosticPremium.css';

export function UploadResults() {
  const { user, profile } = useAuth();
  const { 
    setCurrentStep, setUploadedFile, setInputValid, 
    setUploadedData, setScanId, setErrorMessage, 
    errorMessage, uploadedFile, inputValid, reportTier
  } = useDiagnosticContext();
  
  const [isValidating, setIsValidating] = useState(false);
  const [decodedData, setDecodedData] = useState<PCCData | null>(null);
  const [moduleCount, setModuleCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const processFile = async (file: File) => {
    trackEvent('upload_start', {
      file_name: file.name,
      file_size: file.size
    });

    // Validate extension
    if (!file.name.toLowerCase().endsWith('.pcc')) {
      const errorMsg = `Please upload a .pcc file, not a .${file.name.split('.').pop()} file.`;
      setErrorMessage(errorMsg);
      trackEvent('upload_error', { error_message: errorMsg });
      return;
    }

    // Validate size
    if (file.size < 2 * 1024) {
      const errorMsg = 'This file looks too small. Did the scanner finish running?';
      setErrorMessage(errorMsg);
      trackEvent('upload_error', { error_message: errorMsg });
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      const errorMsg = 'File too large. Please contact support@pccheckr.com.';
      setErrorMessage(errorMsg);
      trackEvent('upload_error', { error_message: errorMsg });
      return;
    }

    try {
      setIsValidating(true);
      setErrorMessage(null);
      setInputValid(false);
      setDecodedData(null);

      const result = await parsePccFile(file);

      // --- Resilience Audit: Module Validation ---
      const requiredModules = ['systeminfo', 'disk', 'ram'];
      const missing = requiredModules.filter(m => !result.rawModules[m as keyof typeof result.rawModules] || result.rawModules[m as keyof typeof result.rawModules].length < 50);
      
      if (missing.length > 0) {
        throw new Error(`Diagnostic file is incomplete. Missing or corrupted: ${missing.join(', ')}. Please run the scanner again.`);
      }

      if (result.valid) {
        // Success — update state with parsed data
        setUploadedFile(file);
        
        // Map result to a format the UI expects (simulating PCCData structure)
        const displayData: any = {
          timestamp: result.timestamp,
          scanId: result.scanId,
          moduleCount: result.moduleCount,
          system: {
            manufacturer: result.manufacturer,
            model: result.model,
            os: result.osName,
            ramGB: result.totalRamGb
          },
          storage: {
            mainDrive: {
              sizeGB: result.diskTotalGb,
              freeGB: result.diskFreeGb
            }
          }
        };

        setDecodedData(displayData);
        setModuleCount(result.moduleCount);
        setScanId(result.scanId);
        setInputValid(true);
        
        trackEvent('upload_success', {
          scan_id: result.scanId,
          module_count: result.moduleCount,
          os_name: result.osName,
          total_ram_gb: result.totalRamGb,
          manufacturer: result.manufacturer
        });
        
        // Set data for context (using JSON string of all raw modules)
        const { msinfo32, ...analyzableModules } = result.rawModules;
        setUploadedData(JSON.stringify(analyzableModules), 'pcc');
      }
    } catch (err: any) {
      console.error("[UploadResults] Process error:", err);
      // Friendly error mapping
      let msg = err.message || 'Failed to process scan file.';
      if (msg.includes('Signature')) msg = 'This file does not appear to be a valid PCCheckr scan.';
      if (msg.includes('JSZip')) msg = 'The scan file is corrupted or not a valid archive.';
      
      setErrorMessage(msg);
      setInputValid(false);
      
      trackEvent('upload_error', {
        error_message: msg
      });
    } finally {
      setIsValidating(false);
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="stepContainer fade-up">
      <header className="stepHeader" style={{ textAlign: 'center', marginBottom: '48px' }}>
        <button 
          className="btnSecondary" 
          onClick={() => setCurrentStep('download')}
          style={{ border: 'none', padding: '0', marginBottom: '24px' }}
        >
          <ArrowLeft size={14} /> Back to download
        </button>
        <h1 style={{ fontSize: '32px', fontWeight: 900, marginBottom: '16px' }}>Upload Your Results</h1>
        <p style={{ color: '#9999a8', fontSize: '18px' }}>Upload the .pcc file created by the scanner to begin your AI analysis.</p>
      </header>

      <div className="uploadSection">
        {!inputValid || !uploadedFile ? (
          <div 
            className="premiumCardWrapper"
            style={{ cursor: 'pointer' }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="premiumCardGlow"></div>
            <div className="premiumCardInner" style={{ padding: '60px 40px', border: '2px dashed rgba(255,255,255,0.06)' }}>
              {isValidating ? (
                <div className="uploadStatus">
                  <RefreshCw size={48} className="spin" style={{ color: 'var(--accent)', animation: 'spin 2s linear infinite' }} />
                  <p style={{ marginTop: '16px', fontWeight: 600 }}>Validating file...</p>
                </div>
              ) : (
                <div className="uploadStatus">
                  <div className="iconBox">
                    <Upload size={32} color="var(--accent)" />
                  </div>
                  <h3 style={{ fontSize: '18px', fontWeight: 700, margin: '16px 0 8px' }}>Drop your .pcc file here</h3>
                  <p style={{ color: '#666677', fontSize: '14px' }}>or click to browse your computer</p>
                </div>
              )}
              <input 
                type="file" 
                ref={fileInputRef}
                accept=".pcc" 
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
          </div>
        ) : (
          <div className="uploadSuccessContainer">
            {/* COMPONENT 6: UPLOAD SUCCESS STATE */}
            <div className="checkRing">
              <svg viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" fill="none" className="checkRingBg" />
                <circle cx="32" cy="32" r="28" fill="none" className="checkRingMain" transform="rotate(-90 32 32)" />
                <polyline points="20,32 28,40 44,24" fill="none" stroke="#34d399" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#eaeaf0', marginBottom: '20px' }}>
              Scan Uploaded Successfully
            </h2>

            <div className="metadataCard">
              <div className="metadataGrid">
                <div className="metadataItem">
                  <label>OS Name</label>
                  <span style={{ fontSize: '13px' }}>{decodedData?.system?.os || 'Windows'}</span>
                </div>
                <div className="metadataItem">
                  <label>Memory</label>
                  <span>{decodedData?.system?.ramGB ? `${decodedData.system.ramGB} GB` : 'Detected'}</span>
                </div>
                <div className="metadataItem">
                  <label>Storage</label>
                  <span>{decodedData?.storage?.mainDrive?.sizeGB ? `${decodedData.storage.mainDrive.sizeGB} GB` : 'Detected'}</span>
                </div>
                <div className="metadataItem">
                  <label>Manufacturer</label>
                  <span>{decodedData?.system?.manufacturer || 'Detected'}</span>
                </div>
              </div>

              <div className="validationList">
                <div className="validationItem">
                  <span className="validationCheck">✓</span> PCCheckr scan format verified
                </div>
                <div className="validationItem">
                  <span className="validationCheck">✓</span> Data integrity check passed
                </div>
                <div className="validationItem">
                  <span className="validationCheck">✓</span> {moduleCount} diagnostic modules found
                </div>
                <div className="validationItem">
                  <span className="validationCheck">✓</span> Ready for AI analysis
                </div>
              </div>
            </div>

            {decodedData?.system?.manufacturer && (
              <div className="manufacturerBadge">
                <span>🏭 {decodedData.system.manufacturer} device detected — {reportTier === 'pro' ? 'manufacturer-specific fixes included' : 'upgrade for manufacturer-specific fixes'}</span>
              </div>
            )}

            <div style={{ marginTop: '24px' }}>
              <button 
                className="btnPrimary generateBtn" 
                onClick={() => {
                  trackEvent('generate_report_click', {
                    report_tier: reportTier,
                    scan_id: decodedData?.scanId || 'unknown'
                  });
                  setCurrentStep('report');
                }}
              >
                🤖 Generate AI Report
              </button>
              <div className="tierMetaText">
                {reportTier === 'pro' 
                  ? `Full Pro Report — all categories analysed (1 of ${profile?.credits || 0} credits)` 
                  : 'Free Basic Report — 3 categories analysed'}
              </div>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="errorBox" style={{ 
            marginTop: '20px', 
            padding: '16px', 
            background: 'rgba(239, 68, 68, 0.1)', 
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
            color: '#ef4444',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertCircle size={18} />
            <p>{errorMessage}</p>
          </div>
        )}

        {!inputValid && (
          <div className="helpBox" style={{ marginTop: '32px', textAlign: 'left', padding: '20px', background: 'rgba(17,17,25,0.5)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <RefreshCw size={18} color="#666677" />
              <div>
                <strong style={{ color: '#eaeaf0' }}>Can't find your file?</strong>
                <p style={{ color: '#666677', fontSize: '13px', marginTop: '4px' }}>
                  By default, the scanner saves your report to the same folder where the scanner was run (e.g., your Downloads folder). Look for a file that ends in <strong>.pcc</strong>.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {!inputValid && (
        <footer className="bottomActions" style={{ marginTop: '40px' }}>
          <button className="btnSecondary" onClick={() => setCurrentStep('download')}>
            <ArrowLeft size={14} /> Back
          </button>
        </footer>
      )}
    </div>
  );
}
